import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { timetableService, formatTime12Hour } from '../services/TimetableService';
import './TeacherTimetableView.css';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// 12-hour time slots matching SwiftUI app
const TIME_SLOTS = Array.from({ length: 7 }, (_, i) => {
  const hour = 8 + i;
  const period = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const nextHour = (hour + 1) % 12 === 0 ? 12 : (hour + 1) % 12;
  const nextPeriod = (hour + 1) < 12 ? 'AM' : 'PM';
  return {
    start: `${hour12}:00 ${period}`,
    end: `${nextHour}:00 ${nextPeriod}`
  };
});

function TeacherTimetableView({ teacherData }) {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [timetableStatus, setTimetableStatus] = useState('Draft');
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [activeSlotIndex, setActiveSlotIndex] = useState(null);
  const [sheetRoom, setSheetRoom] = useState('');
  const [sheetStart, setSheetStart] = useState('8:00 AM');
  const [sheetEnd, setSheetEnd] = useState('9:00 AM');

  const loadTeacherClasses = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    try {
      const constraints = [where('teacherId', '==', teacherData.uid)];
      if (teacherData?.schoolId) {
        constraints.push(where('schoolId', '==', teacherData.schoolId));
      }
      const q = query(collection(db, 'classes'), ...constraints);
      const snapshot = await getDocs(q);
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClasses(classesData);
      if (classesData.length > 0 && !selectedClassId) {
        setSelectedClassId(classesData[0].id);
      }
    } catch (error) {
      console.error('TeacherTimetableView: error loading classes', error);
      setMessage('Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, [teacherData?.uid, teacherData?.schoolId, selectedClassId]);

  const loadTimetable = useCallback(async (classId) => {
    setLoading(true);
    setMessage('');
    try {
      const timetable = await timetableService.getTimetable(classId);
      setEntries(timetable.entries || []);
      setTimetableStatus(timetable.status || 'Draft');
    } catch (error) {
      console.error('TeacherTimetableView: error loading timetable', error);
      setMessage('Failed to load timetable');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSubjects = useCallback(async (classId) => {
    try {
      let subjectsQuery = query(collection(db, 'subjects'));
      if (teacherData?.schoolId) {
        subjectsQuery = query(collection(db, 'subjects'), where('schoolId', '==', teacherData.schoolId));
      }
      const snapshot = await getDocs(subjectsQuery);
      const subjectsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Filter to show subjects assigned to this class OR available subjects
      const classSubjects = subjectsData.filter(s => s.classId === classId && s.isActive);
      setSubjects(classSubjects);
    } catch (error) {
      console.error('TeacherTimetableView: error loading subjects', error);
    }
  }, [teacherData?.schoolId]);

  useEffect(() => {
    loadTeacherClasses();
  }, [loadTeacherClasses]);

  useEffect(() => {
    if (selectedClassId) {
      loadTimetable(selectedClassId);
      loadSubjects(selectedClassId);
    }
  }, [selectedClassId, loadTimetable, loadSubjects]);

  const persistEntries = async (updatedEntries) => {
    if (!selectedClassId) return;
    setSaving(true);
    setMessage('');
    try {
      await timetableService.saveTimetable(selectedClassId, updatedEntries, teacherData?.schoolId || null);
      setMessage('Timetable saved');
    } catch (error) {
      console.error('TeacherTimetableView: error saving timetable', error);
      setMessage('Failed to save timetable');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = () => persistEntries(entries);

  const handleDeleteEntry = (entryId) => {
    if (isReadOnly) return;
    const updated = entries.filter(entry => entry.id !== entryId);
    setEntries(updated);
    persistEntries(updated);
  };

  const isReadOnly = timetableStatus === 'Published' || timetableStatus === 'Archived';
  const dayEntries = entries.filter(entry => entry.dayOfWeek === selectedDay);
  const entryForSlotIndex = (index) => dayEntries.find(entry => (entry.slotIndex ?? 0) === index);

  const openSlot = (slotIndex) => {
    if (isReadOnly) return;
    const existing = entryForSlotIndex(slotIndex);
    const slot = TIME_SLOTS[slotIndex];
    setSheetRoom(existing?.room || '');
    setSheetStart(existing?.startTime || slot.start);
    setSheetEnd(existing?.endTime || slot.end);
    setActiveSlotIndex(slotIndex);
  };

  const closeSheet = () => {
    setActiveSlotIndex(null);
    setSheetRoom('');
  };

  const advanceToNextEmptySlot = (slotIndex) => {
    for (let i = slotIndex + 1; i < TIME_SLOTS.length; i++) {
      if (!entryForSlotIndex(i)) {
        setTimeout(() => openSlot(i), 300);
        return;
      }
    }
  };

  const handleSelectSubject = (subject) => {
    if (activeSlotIndex === null || !selectedClassId) return;
    const existing = entryForSlotIndex(activeSlotIndex);
    const trimmedRoom = sheetRoom.trim();

    let updated;
    if (existing) {
      updated = entries.map(entry =>
        entry.id === existing.id
          ? { ...entry, subjectName: subject.name, subjectId: subject.id, room: trimmedRoom, startTime: sheetStart, endTime: sheetEnd }
          : entry
      );
    } else {
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        dayOfWeek: selectedDay,
        subjectName: subject.name,
        subjectId: subject.id,
        startTime: sheetStart,
        endTime: sheetEnd,
        room: trimmedRoom,
        slotIndex: activeSlotIndex
      };
      updated = [...entries, entry];
    }

    setEntries(updated);
    persistEntries(updated);
    const slotIndex = activeSlotIndex;
    closeSheet();
    if (!existing) {
      advanceToNextEmptySlot(slotIndex);
    }
  };

  const handleRemoveActiveEntry = () => {
    const existing = activeSlotIndex !== null && entryForSlotIndex(activeSlotIndex);
    if (existing) {
      handleDeleteEntry(existing.id);
    }
    closeSheet();
  };

  if (loading && classes.length === 0) {
    return (
      <div className="timetable-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="teacher-timetable">
      <div className="timetable-header">
        <h2>Class Timetable</h2>
      </div>

      {classes.length === 0 ? (
        <div className="timetable-empty">
          <p>No classes assigned. You need a class to create a timetable.</p>
        </div>
      ) : (
        <>
          <div className="class-selector">
            {classes.map(cls => (
              <button
                key={cls.id}
                className={`class-chip ${selectedClassId === cls.id ? 'active' : ''}`}
                onClick={() => setSelectedClassId(cls.id)}
              >
                {cls.name} (Grade {cls.grade})
              </button>
            ))}
          </div>

          <div className="day-selector">
            {WEEKDAYS.map(day => (
              <button
                key={day}
                className={`day-chip ${selectedDay === day ? 'active' : ''}`}
                onClick={() => setSelectedDay(day)}
              >
                {day.slice(0, 3)}
              </button>
            ))}
          </div>

          <div className="timetable-day-content">
            <h3>{selectedDay}</h3>
            {loading ? (
              <div className="timetable-loading">
                <div className="spinner"></div>
                <p>Loading timetable...</p>
              </div>
            ) : (
              <div className="entries-list">
                {TIME_SLOTS.map((slot, index) => {
                  const entry = entryForSlotIndex(index);
                  return (
                    <div key={index} className="entry-card" onClick={() => openSlot(index)}>
                      <div className="entry-main">
                        <span className="entry-subject">{entry?.subjectName || 'Free'}</span>
                        <span className="entry-time">{formatTime12Hour(entry?.startTime || slot.start)} - {formatTime12Hour(entry?.endTime || slot.end)}</span>
                        {entry?.room && <span className="entry-room">Room {entry.room}</span>}
                      </div>
                      {!isReadOnly && (
                        <button className="edit-button" onClick={(e) => { e.stopPropagation(); openSlot(index); }}>
                          Edit
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {message && <p className={`timetable-message ${message.includes('Failed') ? 'error' : 'success'}`}>{message}</p>}
          </div>

          {!isReadOnly && (
            <div className="timetable-actions">
              {saving && <div className="spinner"></div>}
              <button onClick={handleSaveAll} disabled={saving}>
                Save Timetable
              </button>
            </div>
          )}
        </>
      )}

      {activeSlotIndex !== null && (
        <div className="sheet-overlay" onClick={closeSheet}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h3>
              {selectedDay} • Period {activeSlotIndex + 1}
            </h3>
            <div className="sheet-content">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="text"
                  value={sheetStart}
                  onChange={(e) => setSheetStart(e.target.value)}
                  placeholder="8:00 AM"
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input
                  type="text"
                  value={sheetEnd}
                  onChange={(e) => setSheetEnd(e.target.value)}
                  placeholder="9:00 AM"
                />
              </div>
              <div className="form-group">
                <label>Room (optional)</label>
                <input
                  type="text"
                  value={sheetRoom}
                  onChange={(e) => setSheetRoom(e.target.value)}
                  placeholder="Room number"
                />
              </div>

              <h4>Select Subject</h4>
              {subjects.length === 0 ? (
                <p className="no-subjects">No subjects available for this class</p>
              ) : (
                <div className="subjects-list">
                  {subjects.map(subject => (
                    <button
                      key={subject.id}
                      className="subject-button"
                      onClick={() => handleSelectSubject(subject)}
                    >
                      {subject.name}
                    </button>
                  ))}
                </div>
              )}

              {entryForSlotIndex(activeSlotIndex) && (
                <button className="remove-entry-button" onClick={handleRemoveActiveEntry}>
                  Remove Entry
                </button>
              )}
            </div>
            <div className="sheet-actions">
              <button onClick={closeSheet}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherTimetableView;
